import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdVisibility, MdEdit, MdLocationOn } from 'react-icons/md';
import ImageCropper from '../components/ImageCropper';
import WebPreviewModal from '../components/WebPreviewModal';
import DragDropImageUpload from '../components/DragDropImageUpload';
import GuidelineReviewModal from '../components/GuidelineReviewModal';
import ReaderCheckModal from '../components/ReaderCheckModal';
import { useAuth } from '../context/AuthContext';
import { statesMatch, ALL_INDIA_STATE } from '../utils/format';
import { reviewArticleWithAI, checkReaderComprehension } from '../api/openai';

const ZONE_PREF_KEY = 'zonal_article_preference';

function loadZonePref() {
  try {
    const raw = localStorage.getItem(ZONE_PREF_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveZonePref(pref) {
  try {
    if (pref && typeof pref === 'object') {
      const existing = loadZonePref() || {};
      localStorage.setItem(ZONE_PREF_KEY, JSON.stringify({ ...existing, ...pref }));
    }
  } catch {
    // Ignore localStorage errors
  }
}

function clearZonePref() {
  localStorage.removeItem(ZONE_PREF_KEY);
}

function toLocalDatetimeString(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function freshForm(zonePref) {
  return {
    title: '', summary: '', source_url: '', source: '', media_type: 'image',
    image_url: '', video_url: '', thumbnail_url: '', images: [],
    category: zonePref.category, city: zonePref.city, is_breaking: false, is_featured: false, zone_only: true, breaking_state: ALL_INDIA_STATE,
    published_at: toLocalDatetimeString(new Date()),
    slug: '',
    author_name: '',
    status: 'published',
    manual_thumbnail: false,
    title_en: '', summary_en: '',
    title_hi: '', summary_hi: '',
    custom_notification_title: '',
  };
}

// ---- Step 1: pick & save the zone (category / state / city) ----
function ZonePreferenceScreen({ onSaved }) {
  const saved = loadZonePref() || {};
  const [categories, setCategories] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [states, setStates] = useState([]);
  const [category, setCategory] = useState(saved.category || '');
  const [state, setState] = useState(saved.state || '');
  const [city, setCity] = useState(saved.city || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/categories'), api.get('/cities')])
      .then(([catRes, cityRes]) => {
        const catList = catRes.data.data || [];
        setCategories(catList);

        const all = cityRes.data.data || [];
        setAllCities(all);
        const rawStates = all.map(c => (c.state ? c.state.trim() : null)).filter(Boolean);
        setStates([...new Set(rawStates)]);

        const currentSaved = loadZonePref();
        if (currentSaved) {
          if (currentSaved.category) setCategory(currentSaved.category);
          if (currentSaved.state) setState(currentSaved.state);
          if (currentSaved.city) setCity(currentSaved.city);
        }
      })
      .catch(() => toast.error('Failed to load categories/cities'))
      .finally(() => setLoading(false));
  }, []);

  const citiesForState = allCities.filter(c => c.state && statesMatch(c.state, state));

  const handleCategoryChange = (val) => {
    setCategory(val);
    saveZonePref({ category: val });
  };

  const handleStateChange = (val) => {
    setState(val);
    setCity('');
    saveZonePref({ state: val, city: '' });
  };

  const handleCityChange = (val) => {
    setCity(val);
    saveZonePref({ city: val });
  };

  const canContinue = Boolean(category && state && city);

  const handleContinue = () => {
    if (!canContinue) return toast.error('Please select category, state and city');
    const pref = { category, state, city, confirmed: true };
    saveZonePref(pref);
    onSaved(pref);
  };

  return (
    <div className="page" style={{ paddingLeft: 16, paddingRight: 16 }}>
      <div style={{ maxWidth: 520, margin: '40px auto' }}>
        <div className="pub-panel" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 28, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <MdLocationOn size={22} color="#EF233C" />
            <h2 style={{ margin: 0, fontSize: 20 }}>Set Your Zone</h2>
          </div>
          <p style={{ margin: '4px 0 20px', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}>
            Pick the category, state and city you'll be posting zonal news for. We'll remember this
            so every new zonal article starts pre-filled — no need to reselect it each time.
          </p>

          <div className="form-group">
            <label>Category</label>
            <select value={category} onChange={e => handleCategoryChange(e.target.value)} disabled={loading}>
              <option value="">Select category</option>
              {categories.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Sub Category</label>
            <select value={state} onChange={e => handleStateChange(e.target.value)} disabled={loading}>
              <option value="">Select sub category</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>City</label>
            <select value={city} onChange={e => handleCityChange(e.target.value)} disabled={loading || !state}>
              <option value="">{state ? 'Select city' : 'Select a state first'}</option>
              {citiesForState.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>

          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: 8 }}
            onClick={handleContinue}
            disabled={!canContinue || loading}
          >
            Continue to Create Zonal Article →
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Step 2: the actual publish form, zone locked from preference ----
function ZonalPublishForm({ zonePref, onChangeZone }) {
  const { admin } = useAuth();
  const [form, setForm] = useState(() => freshForm(zonePref));

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [showWebPreview, setShowWebPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [cropper, setCropper] = useState({ active: false, image: null, type: null });
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [viewMatchedArticle, setViewMatchedArticle] = useState(null);
  const [reviewModal, setReviewModal] = useState({ open: false, loading: false, error: null, result: null });
  const [readerCheck, setReaderCheck] = useState({ open: false, loading: false, error: null, questions: [] });

  const handleChangeZone = () => {
    if (form.title.trim() || form.summary.trim()) {
      if (!window.confirm('You have an unsaved draft. Changing zone will discard it. Continue?')) return;
    }
    onChangeZone();
  };

  const handleGalleryFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    setGalleryUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data?.data?.url) {
          newUrls.push(res.data.data.url);
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (newUrls.length > 0) {
      setForm(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newUrls]
      }));
      toast.success(`${newUrls.length} gallery image(s) uploaded`);
    }
    setGalleryUploading(false);
  };

  const moveGalleryImage = (index, direction) => {
    setForm(prev => {
      const arr = [...(prev.images || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return prev;
      const temp = arr[index];
      arr[index] = arr[targetIndex];
      arr[targetIndex] = temp;
      return { ...prev, images: arr };
    });
  };

  const removeGalleryImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
  };

  const handleGalleryPasteImage = (e) => {
    const items = e.clipboardData?.items || [];
    const filesToUpload = [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          filesToUpload.push(file);
        }
      }
    }
    if (filesToUpload.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      handleGalleryFilesUpload(filesToUpload);
    }
  };

  const wordCount = form.summary.trim().split(/\s+/).filter(Boolean).length;

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const validate = () => {
    const errs = {};
    if (form.title.length > 75) errs.title = 'Title cannot exceed 75 characters.';
    if (form.summary.length > 400) errs.summary = 'Summary cannot exceed 400 characters.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';

      window._tempFileInput = input;

      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const file = files[0];
        if (currentType === 'video') {
          uploadToServer(file, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(file);
        }
      };

      input.click();
      return;
    }

    const file = fileOrType;
    if (type === 'video') {
      uploadToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCropComplete = async (blob) => {
    const type = cropper.type;
    setCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadToServer(file, type);
  };

  const uploadToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = (type === 'image' || type === 'extra_image') ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      const thumbnailUrl = res.data.data.thumbnail_url;

      if (type === 'image') set('image_url', url);
      else if (type === 'extra_image') setForm(prev => ({ ...prev, images: [...(prev.images || []), url] }));
      else if (type === 'thumbnail') {
        set('thumbnail_url', url);
        set('manual_thumbnail', true);
      }
      else if (type === 'video') {
        setForm(f => {
          const nextForm = { ...f, video_url: url };
          if (thumbnailUrl && !f.manual_thumbnail) {
            nextForm.thumbnail_url = thumbnailUrl;
          }
          return nextForm;
        });
      }
      toast.success(`${type === 'extra_image' ? 'Image' : type} uploaded and resized!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (form.media_type !== 'image') return;
      if (cropper.active || showWebPreview) return;

      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = () => {
              setCropper({ active: true, image: reader.result, type: 'image' });
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [form.media_type, cropper.active, showWebPreview]);

  const handlePasteImage = (e) => {
    e.preventDefault();
  };

  const handleAiGenerate = async (type, extraData = null) => {
    if (type === 'slug' && !form.title) {
      return toast.error('Please enter a title first to generate a slug');
    }
    if (type === 'transliterate' && !extraData) {
      return toast.error('Please enter some text to convert');
    }

    setLoading(true);
    try {
      const payload = { type };
      if (type === 'slug') payload.title = form.title;
      if (type === 'transliterate') payload.text = extraData;

      const res = await api.post('/admin/ai/generate', payload);
      if (res.data.success) {
        if (type === 'slug') set('slug', res.data.data);
        if (type === 'transliterate') {
          return res.data.data;
        }
        toast.success('Slug generated');
      }
    } catch (err) {
      toast.error('AI operation failed');
    } finally {
      setLoading(false);
    }
  };

  const resetFormForNextArticle = () => {
    setForm(freshForm(zonePref));
    setErrors({});
    setDuplicateWarning(null);
    setViewMatchedArticle(null);
    setReviewModal({ open: false, loading: false, error: null, result: null });
    setReaderCheck({ open: false, loading: false, error: null, questions: [] });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const doPublish = async (overrides = {}) => {
    setLoading(true);
    try {
      const effectiveZoneOnly = form.is_breaking
        ? (form.breaking_state === 'zone_only')
        : true;
      const payload = { ...form, zone_only: effectiveZoneOnly, ...overrides };
      await api.post('/admin/articles', payload);
      toast.success(
        (admin?.role === 'reporter' ? 'Draft saved!' : 'Article published!') + ' Ready for the next zonal article.'
      );
      resetFormForNextArticle();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to publish');
    } finally {
      setLoading(false);
    }
  };

  const runGuidelineReview = async () => {
    setReviewModal({ open: true, loading: true, error: null, result: null });
    try {
      const result = await reviewArticleWithAI({ title: form.title, summary: form.summary });
      setReviewModal({ open: true, loading: false, error: null, result });
    } catch (err) {
      setReviewModal({ open: true, loading: false, error: err.message || 'AI review failed', result: null });
    }
  };

  const closeReviewModal = () => setReviewModal({ open: false, loading: false, error: null, result: null });

  const runReaderCheck = async () => {
    setReaderCheck({ open: true, loading: true, error: null, questions: [] });
    try {
      const result = await checkReaderComprehension({ title: form.title, summary: form.summary });
      if (result.complete) {
        setReaderCheck({ open: false, loading: false, error: null, questions: [] });
        await runGuidelineReview();
      } else {
        setReaderCheck({ open: true, loading: false, error: null, questions: result.questions });
      }
    } catch (err) {
      setReaderCheck({ open: true, loading: false, error: err.message || 'Reader check failed', questions: [] });
    }
  };

  const closeReaderCheck = () => setReaderCheck({ open: false, loading: false, error: null, questions: [] });

  const handleReaderContinue = async () => {
    closeReaderCheck();
    await runGuidelineReview();
  };

  const handlePublishDirectly = () => {
    closeReaderCheck();
    doPublish();
  };

  const handleUseCorrected = () => {
    const { corrected } = reviewModal.result;
    const cleanTitle = (corrected?.title || '').slice(0, 75);
    const cleanSummary = (corrected?.body || '').slice(0, 400);
    set('title', cleanTitle);
    set('summary', cleanSummary);
    closeReviewModal();
    doPublish({ title: cleanTitle, summary: cleanSummary });
  };

  const handleUseOriginal = () => {
    closeReviewModal();
    doPublish();
  };

  const handlePublishWithoutReview = () => {
    closeReviewModal();
    doPublish();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.summary) return toast.error('Title and summary required');
    if (form.title.length > 75) return toast.error(`Title exceeds limit by ${form.title.length - 75} characters`);
    if (form.summary.length > 400) return toast.error(`Summary exceeds limit by ${form.summary.length - 400} characters`);
    if (!validate()) return toast.error('Please fix validation errors');

    // Check if AI Check is disabled in Admin Settings
    try {
      const res = await api.get('/admin/settings/ai-check');
      if (res.data?.success && res.data.data?.enabled === false) {
        // AI check is OFF: skip AI check and publish zonal article directly through existing flow
        return doPublish();
      }
    } catch (err) {
      // If error fetching setting, continue with default flow
    }

    await runReaderCheck();
  };

  const handleTransliterate = async (field) => {
    const text = form[field];
    if (!text) return toast.error(`Please type something in ${field} first`);
    const result = await handleAiGenerate('transliterate', text);
    if (result) {
      const limit = field === 'title' ? 75 : field === 'summary' ? 400 : result.length;
      set(field, result.slice(0, limit));
    }
  };

  const handleGenerateTranslations = async () => {
    if (!form.title || !form.summary) {
      return toast.error('Please enter Telugu title and summary first');
    }

    setLoading(true);
    let successCount = 0;

    try {
      const enRes = await api.post('/admin/ai/generate', { type: 'translate', title: form.title, summary: form.summary, targetLanguage: 'English' });

      if (enRes.data.success && enRes.data.data.headline && enRes.data.data.body) {
        set('title_en', enRes.data.data.headline.slice(0, 150));
        set('summary_en', enRes.data.data.body.slice(0, 600));
        successCount++;
      } else {
        toast.error('English translation failed');
      }
    } catch (err) {
      toast.error('English translation error');
    }

    try {
      const hiRes = await api.post('/admin/ai/generate', { type: 'translate', title: form.title, summary: form.summary, targetLanguage: 'Hindi' });

      if (hiRes.data.success && hiRes.data.data.headline && hiRes.data.data.body) {
        set('title_hi', hiRes.data.data.headline.slice(0, 150));
        set('summary_hi', hiRes.data.data.body.slice(0, 600));
        successCount++;
      } else {
        toast.error('Hindi translation failed');
      }
    } catch (err) {
      toast.error('Hindi translation error');
    }

    setLoading(false);
    if (successCount > 0) toast.success('Translations generated successfully!');
  };

  const handleCopySingleTranslation = async (lang, title, summary) => {
    try {
      const reviewPrompt = "Please review this news as a journalist and an experienced news writer. Improve the content wherever necessary by correcting grammar, punctuation, sentence structure, clarity, factual consistency, and readability while following professional news editorial style guidelines. Preserve the original meaning and tone. Return only the revised headline and article.\n\n";
      const copyText = `${reviewPrompt}${lang} Headline:\n${title || ''}\n\n${lang} Article:\n${summary || ''}`;

      await navigator.clipboard.writeText(copyText);
      toast.success(`${lang} translation copied successfully!`);
    } catch (err) {
      toast.error(`Failed to copy ${lang.toLowerCase()} translation.`);
    }
  };

  return (
    <div className="page" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
      <div className="publish-layout" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Create New Zonal Article</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              className="pub-inline-select"
              value={form.status}
              onChange={e => set('status', e.target.value)}
              disabled={admin?.role === 'reporter'}
              style={{ fontSize: '13px', padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card)', color: 'var(--text)', cursor: 'pointer' }}
            >
              <option value="draft">📋 Draft</option>
              <option value="published">✓ Published</option>
            </select>
            <button
              type="button"
              className="btn btn-outline-red"
              onClick={() => setShowWebPreview(true)}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              👁️ Preview
            </button>
            <button
              type="submit"
              form="zonal-publish-form"
              className="btn btn-primary"
              disabled={loading || checkingDuplicate || readerCheck.loading || reviewModal.loading || form.title.length > 75 || form.summary.length > 400}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              {checkingDuplicate ? '🔍 Checking duplicates...' : readerCheck.loading ? '📖 Reading...' : reviewModal.loading ? '🤖 Reviewing...' : loading ? '⏳ Publishing...' : '✓ Publish'}
            </button>
          </div>
        </div>

        {/* Locked zone bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 10,
          padding: '10px 16px', marginBottom: 16
        }}>
          <MdLocationOn color="#0369A1" size={18} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#0369A1' }}>Zone:</span>
          <span style={{ fontSize: 12, fontWeight: 600, background: '#E0F2FE', color: '#0369A1', padding: '3px 10px', borderRadius: 20 }}>{zonePref.category}</span>
          <span style={{ fontSize: 12, fontWeight: 600, background: '#E0F2FE', color: '#0369A1', padding: '3px 10px', borderRadius: 20 }}>{zonePref.state}</span>
          <span style={{ fontSize: 12, fontWeight: 600, background: '#E0F2FE', color: '#0369A1', padding: '3px 10px', borderRadius: 20 }}>{zonePref.city}</span>
          <span style={{ fontSize: 12, fontWeight: 600, background: '#DCFCE7', color: '#15803D', padding: '3px 10px', borderRadius: 20 }}>Show only in zone</span>
          <button
            type="button"
            onClick={handleChangeZone}
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: '1px solid #BAE6FD', color: '#0369A1',
              borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer'
            }}
          >
            <MdEdit size={14} /> Change Zone
          </button>
        </div>

        <form id="zonal-publish-form" className="publish-form" onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'flex-start', maxWidth: '100%' }}>
          <div className="pub-panel" style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}>
            <div className="form-group">
              <div className="flex-row justify-between items-center mb-1">
                <label className="mb-0">Title</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    color: form.title.length > 75 ? '#DC2626' : '#6B7280',
                    transition: 'color 0.2s'
                  }}>
                    {form.title.length} / 75
                  </span>
                  {form.title.length > 75 && (
                    <span style={{
                      fontSize: '12px',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '400',
                      animation: 'pulse 1s infinite'
                    }}>
                      +{form.title.length - 75} extra
                    </span>
                  )}
                </div>
              </div>
              <input
                type="text"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Type in English (e.g. namaskaram)..."
                style={{
                  borderColor: form.title.length > 75 ? '#DC2626' : undefined,
                  backgroundColor: form.title.length > 75 ? '#FEF2F2' : undefined
                }}
                required
              />
              {form.title.length > 75 && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px', display: 'block' }}>
                  ⚠️ Title exceeds limit by {form.title.length - 75} characters. Please trim it down to 75 characters max.
                </span>
              )}
              {errors.title && <span className="field-error">{errors.title}</span>}
            </div>

            <div className="form-group">
              <div className="flex-row justify-between items-center mb-1">
                <label className="mb-0">Summary</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    color: form.summary.length > 400 ? '#DC2626' : '#6B7280',
                    transition: 'color 0.2s'
                  }}>
                    {form.summary.length} / 400
                  </span>
                  {form.summary.length > 400 && (
                    <span style={{
                      fontSize: '12px',
                      background: '#FEE2E2',
                      color: '#DC2626',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '400',
                      animation: 'pulse 1s infinite'
                    }}>
                      +{form.summary.length - 400} extra
                    </span>
                  )}
                </div>
              </div>
              <textarea
                value={form.summary}
                onChange={(e) => set('summary', e.target.value)}
                placeholder="Type in English..."
                rows={7}
                style={{
                  borderColor: form.summary.length > 400 ? '#DC2626' : undefined,
                  backgroundColor: form.summary.length > 400 ? '#FEF2F2' : undefined,
                  resize: 'vertical'
                }}
                required
              />
              {form.summary.length > 400 && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '6px', display: 'block' }}>
                  ⚠️ Summary exceeds limit by {form.summary.length - 400} characters. Please trim it down to 400 characters max.
                </span>
              )}
              {errors.summary && <span className="field-error">{errors.summary}</span>}
            </div>

            {(form.title_en || form.summary_en) && (
              <div className="form-group" style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: 'var(--text-secondary)' }}>English Translation</h4>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    onClick={() => handleCopySingleTranslation('English', form.title_en, form.summary_en)}
                    style={{ border: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '12px' }}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                <div className="form-group mb-sm">
                  <label className="mb-0">Title (English)</label>
                  <input
                    type="text"
                    value={form.title_en}
                    onChange={(e) => set('title_en', e.target.value)}
                    placeholder="English title..."
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="mb-0">Summary (English)</label>
                  <textarea
                    value={form.summary_en}
                    onChange={(e) => set('summary_en', e.target.value)}
                    placeholder="English summary..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {(form.title_hi || form.summary_hi) && (
              <div className="form-group" style={{ padding: '16px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: 'var(--text-secondary)' }}>Hindi Translation</h4>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    onClick={() => handleCopySingleTranslation('Hindi', form.title_hi, form.summary_hi)}
                    style={{ border: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '12px' }}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                <div className="form-group mb-sm">
                  <label className="mb-0">Title (Hindi)</label>
                  <input
                    type="text"
                    value={form.title_hi}
                    onChange={(e) => set('title_hi', e.target.value)}
                    placeholder="Hindi title..."
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="mb-0">Summary (Hindi)</label>
                  <textarea
                    value={form.summary_hi}
                    onChange={(e) => set('summary_hi', e.target.value)}
                    placeholder="Hindi summary..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            <div className="form-group" style={{ display: 'none' }}>
              <label>Publish Date & Time</label>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={(e) => set('published_at', e.target.value)}
              />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ margin: 0 }}>Media Type</label>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>720 × 590 px</span>
              </div>
              <div className="radio-group">
                <label className={`radio-card ${form.media_type === 'image' ? 'selected' : ''}`}>
                  <input type="radio" value="image" checked={form.media_type === 'image'}
                    onChange={() => set('media_type', 'image')} />
                  Image
                </label>
                <label className={`radio-card ${form.media_type === 'video' ? 'selected' : ''}`}>
                  <input type="radio" value="video" checked={form.media_type === 'video'}
                    onChange={() => set('media_type', 'video')} />
                  Video
                </label>
              </div>
            </div>

            {form.media_type === 'image' && (
              <>
                <div className="form-group">
                  <label>Article Image (Web & App)</label>
                  <div className="upload-area" tabIndex={0} onClick={() => handleFileSelect('image')} onPaste={handlePasteImage}>
                    {form.image_url ? (
                      <img src={form.image_url} alt="preview" className="upload-preview" />
                    ) : (
                      <p>{uploading ? 'Processing...' : 'Click to select & crop — or paste an image (Ctrl+V)'}</p>
                    )}
                  </div>
                  <input type="text" value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="Or paste image URL" className="mt-sm" style={{ display: 'none' }} />
                </div>

                <div className="form-group" style={{ marginTop: '20px' }} tabIndex={0} onPaste={handleGalleryPasteImage}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ margin: 0, fontWeight: 700 }}>Gallery Images</label>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Click, drag & drop, or paste (Ctrl+V)</span>
                  </div>
                  <div
                    className="upload-area"
                    tabIndex={0}
                    onPaste={handleGalleryPasteImage}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.multiple = true;
                      input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                      input.onchange = (e) => handleGalleryFilesUpload(Array.from(e.target.files));
                      input.click();
                    }}
                    style={{ cursor: 'pointer', padding: '24px 16px', border: '2px dashed #CBD5E1', borderRadius: '10px', textAlign: 'center', backgroundColor: 'var(--bg)' }}
                  >
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      {galleryUploading ? 'Uploading gallery images...' : 'Click to select multiple images, drag & drop, or paste (Ctrl+V)'}
                    </p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    {(form.images || []).map((imgUrl, index) => (
                      <div key={index} style={{ position: 'relative', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden', backgroundColor: 'var(--card)', height: '108px', display: 'flex', flexDirection: 'column' }}>
                        <img src={typeof imgUrl === 'string' ? imgUrl : imgUrl.image_url || imgUrl.url} alt={`gallery-${index}`} style={{ width: '100%', height: '80px', objectFit: 'cover', display: 'block' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', height: '28px', boxSizing: 'border-box' }}>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <button
                              type="button"
                              disabled={index === 0}
                              onClick={(e) => { e.stopPropagation(); moveGalleryImage(index, -1); }}
                              style={{ border: 'none', background: 'transparent', cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                              title="Move Left"
                            >
                              ◀
                            </button>
                            <button
                              type="button"
                              disabled={index === (form.images || []).length - 1}
                              onClick={(e) => { e.stopPropagation(); moveGalleryImage(index, 1); }}
                              style={{ border: 'none', background: 'transparent', cursor: index === (form.images || []).length - 1 ? 'default' : 'pointer', opacity: index === (form.images || []).length - 1 ? 0.3 : 1, fontSize: '12px', padding: '2px 4px' }}
                              title="Move Right"
                            >
                              ▶
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeGalleryImage(index); }}
                            style={{ border: 'none', background: 'transparent', color: '#EF233C', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', padding: '2px 4px' }}
                            title="Remove Image"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}

                    <div
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                        input.onchange = (e) => handleGalleryFilesUpload(Array.from(e.target.files));
                        input.click();
                      }}
                      style={{
                        height: '108px',
                        border: '2px dashed #CBD5E1',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: 'var(--bg)',
                        transition: 'all 0.2s ease',
                        color: 'var(--text-secondary)',
                        boxSizing: 'border-box'
                      }}
                      title="Add Images"
                    >
                      <span style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>Add Images</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {form.media_type === 'video' && (
              <>
                <div className="form-group">
                  <label>Video URL</label>
                  <input type="text" value={form.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="MP4 URL..." />
                  <button type="button" className="btn btn-xs mt-xs" onClick={() => handleFileSelect('video')}>Upload Video</button>
                </div>
                <div className="form-group">
                  <label>Video Thumbnail (App & Web)</label>
                  <DragDropImageUpload
                    type="thumbnail"
                    uploading={uploading}
                    imageUrl={form.thumbnail_url}
                    onFileSelect={handleFileSelect}
                  />
                  <input type="text" value={form.thumbnail_url} onChange={(e) => set('thumbnail_url', e.target.value)}
                    placeholder="Or paste thumbnail URL" className="mt-sm" style={{ display: 'none' }} />
                </div>
              </>
            )}
          </div>

          <div className="pub-panel" style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}>
            <div className="form-group">
              <label>Category, State &amp; City</label>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                Locked to your saved zone (<strong>{zonePref.category}</strong> · <strong>{zonePref.state}</strong> · <strong>{zonePref.city}</strong>).
                Use <strong>Change Zone</strong> above to post to a different one.
              </div>
            </div>

            <div className="form-group flex-row gap-8" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.is_breaking}
                    onChange={(e) => set('is_breaking', e.target.checked)} />
                  <span className="breaking-tag">Breaking News</span>
                </label>
              </div>
              <div style={{ flex: 1 }}>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.is_featured}
                    onChange={(e) => set('is_featured', e.target.checked)} />
                  <span className="featured-tag">Featured Article</span>
                </label>
              </div>
            </div>

            {form.is_breaking && (
              <div className="location-picker-row">
                <span className="location-picker-label">Show Breaking News For</span>
                <div className="pill-radio-row">
                  {[
                    { key: 'తెలంగాణ', label: 'Telangana' },
                    { key: 'ఆంధ్రప్రదేశ్', label: 'Andhra Pradesh' },
                    { key: ALL_INDIA_STATE, label: 'All (Telangana + Andhra Pradesh)' },
                    { key: 'zone_only', label: 'Show Only in Zone' }
                  ].map(option => {
                    const isSelected = (form.breaking_state || ALL_INDIA_STATE).trim() === option.key.trim();
                    return (
                      <label key={option.key} className={`pill-radio${isSelected ? ' selected' : ''}`}>
                        <input
                          type="radio"
                          name="zonal_breaking_state"
                          checked={isSelected}
                          onChange={() => {
                            set('breaking_state', option.key);
                            if (option.key === 'zone_only') {
                              set('zone_only', true);
                            } else {
                              set('zone_only', false);
                            }
                          }}
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {form.is_breaking && (
              <div className="form-group">
                <label>Custom Notification Title (Optional)</label>
                <input
                  type="text"
                  value={form.custom_notification_title || ''}
                  onChange={(e) => set('custom_notification_title', e.target.value)}
                  placeholder="Example: Heavy Rain Alert in Hyderabad"
                />
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                  Leave blank to use the original article title.
                </span>
              </div>
            )}
          </div>
        </form>

        <div className="publish-sidebar"></div>
      </div>

      {showWebPreview && (
        <WebPreviewModal
          article={form}
          onClose={() => setShowWebPreview(false)}
        />
      )}

      {cropper.active && (
        <ImageCropper
          image={cropper.image}
          onCropComplete={handleCropComplete}
          onCancel={() => setCropper({ active: false, image: null, type: null })}
        />
      )}

      <ReaderCheckModal
        open={readerCheck.open}
        loading={readerCheck.loading}
        error={readerCheck.error}
        questions={readerCheck.questions}
        onContinue={handleReaderContinue}
        onGoBack={closeReaderCheck}
        onRetry={runReaderCheck}
        onPublishDirectly={handlePublishDirectly}
      />

      <GuidelineReviewModal
        open={reviewModal.open}
        loading={reviewModal.loading}
        error={reviewModal.error}
        result={reviewModal.result}
        original={{ title: form.title, summary: form.summary }}
        onUseCorrected={handleUseCorrected}
        onUseOriginal={handleUseOriginal}
        onPublishWithoutReview={handlePublishWithoutReview}
        onRetry={runGuidelineReview}
        onClose={closeReviewModal}
      />

      {duplicateWarning && (
        <div className="modal-overlay" onClick={() => setDuplicateWarning(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ background: '#FEF2F2', padding: '20px 24px', borderBottom: '1px solid #FEE2E2', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>Duplicate Article Found</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#B91C1C', marginTop: 2 }}>
                  This looks very similar to an already-published article.
                </p>
              </div>
              <button className="modal-close" onClick={() => setDuplicateWarning(null)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                {duplicateWarning.duplicates.length} similar article{duplicateWarning.duplicates.length > 1 ? 's' : ''} found — review before publishing.
              </p>

              {duplicateWarning.duplicates.map((dup) => (
                <div key={dup.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Similarity Score
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#E11D48', padding: '2px 10px', background: '#FFE4E6', borderRadius: 20 }}>
                      {dup.score}%
                    </span>
                  </div>

                  <div className="dup-title" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6, lineHeight: 1.4 }}>
                    {dup.title}
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    <div><strong>Reporter:</strong> {dup.reporter_name || 'Admin'}</div>
                    <div><strong>Category:</strong> {dup.category}</div>
                    <div><strong>Published Date:</strong> {new Date(dup.published_at).toLocaleDateString()}</div>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: '#0284C7', color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setViewMatchedArticle(dup)}
                  >
                    <MdVisibility size={14} /> View Article
                  </button>
                </div>
              ))}
            </div>

            <div className="modal-footer" style={{ padding: '16px 24px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                className="btn btn-ghost"
                onClick={() => setDuplicateWarning(null)}
              >
                Edit Article
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  setDuplicateWarning(null);
                  doPublish();
                }}
                disabled={loading}
              >
                {loading ? 'Publishing...' : 'Publish Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewMatchedArticle && (
        <div className="modal-overlay" onClick={() => setViewMatchedArticle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, borderRadius: 16, overflow: 'hidden' }}>
            <div className="modal-header" style={{ background: 'var(--bg)', padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>
                Matched Duplicate Article Details (ID: #{viewMatchedArticle.id})
              </h3>
              <button className="modal-close" onClick={() => setViewMatchedArticle(null)}><MdClose /></button>
            </div>

            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFF1F2', border: '1px solid #FECDD3', padding: '10px 14px', borderRadius: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#881337' }}>Similarity Score</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#E11D48' }}>{viewMatchedArticle.score}% Match</span>
              </div>

              {(viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url) && (
                <img
                  src={viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url}
                  alt=""
                  style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }}
                />
              )}

              <h4 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4 }}>
                {viewMatchedArticle.title}
              </h4>

              {viewMatchedArticle.summary && (
                <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, marginBottom: 16, background: 'var(--bg)', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
                  {viewMatchedArticle.summary}
                </p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12.5, color: 'var(--text-secondary)', background: 'var(--bg)', padding: 12, borderRadius: 10 }}>
                <div><strong>Article ID:</strong> #{viewMatchedArticle.id}</div>
                <div><strong>Reporter:</strong> {viewMatchedArticle.reporter_name || 'Admin'}</div>
                <div><strong>Category:</strong> {viewMatchedArticle.category || 'General'}</div>
                <div><strong>Published Date:</strong> {new Date(viewMatchedArticle.published_at).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '14px 20px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {viewMatchedArticle.slug ? (
                <a
                  href={`http://localhost:3000/${viewMatchedArticle.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                  style={{ borderColor: '#0284C7', color: '#0284C7', fontSize: 13 }}
                >
                  Open Web Article ↗
                </a>
              ) : <div />}
              <button className="btn btn-primary" onClick={() => setViewMatchedArticle(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ZonalArticles() {
  const [zonePref, setZonePref] = useState(() => {
    const p = loadZonePref();
    return (p && p.category && p.state && p.city && p.confirmed !== false) ? p : null;
  });

  if (!zonePref) {
    return <ZonePreferenceScreen onSaved={(pref) => setZonePref(pref)} />;
  }

  return (
    <ZonalPublishForm
      key={`${zonePref.category}|${zonePref.state}|${zonePref.city}`}
      zonePref={zonePref}
      onChangeZone={() => {
        saveZonePref({ ...zonePref, confirmed: false });
        setZonePref(null);
      }}
    />
  );
}
